import { FormControl, ReactiveFormsModule } from '@angular/forms';
import {
    debounceTime,
    distinctUntilChanged,
    filter,
    map,
    merge,
    Subject,
} from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
    Component,
    DestroyRef,
    input,
    OnChanges,
    OnDestroy,
    OnInit,
    output,
    signal,
    SimpleChanges,
} from '@angular/core';
import {
    TextInput,
    TextInputAction,
} from '../input/text-input/text-input.component';

/**
 * Evento que é lançado quando ocorre uma pesquisa.
 */
export type SearchInputEvent = {
    query: string;
};

/**
 * Evento que é lançado quando o usuário clica no botão de cancelar.
 */
export type SearchInputCancelEvent = {
    lastQuery: string | null;
};

/**
 * Campo de busca com botões de pesquisar/cancelar.
 *
 * Suporta busca em tempo real (com debounce) ou manual (Enter/botão). Emite
 * {@link search} ao pesquisar e {@link cancel} ao cancelar (Esc/botão),
 * respeitando o tamanho mínimo de texto ({@link minSize}).
 */
@Component({
    selector: 'ai-search-input',
    standalone: true,
    templateUrl: './search-input.component.html',
    styleUrls: ['./search-input.component.scss'],
    imports: [ReactiveFormsModule, TextInput],
})
export class SearchInput implements OnInit, OnChanges, OnDestroy {
    /** Tamanho mínimo padrão de caracteres para emitir a busca. */
    public static MIN_SIZE = 1;

    /** Define se a busca é disparada durante a digitação (com debounce). */
    readonly realTimeSearch = input<boolean>(false);

    /** Tamanho mínimo de caracteres para emitir o evento de busca. */
    readonly minSize = input<number | null | undefined>(null);

    /** Placeholder da caixa de busca. */
    readonly placeholder = input<string>('Pesquisar');

    /** Indica se o campo está desabilitado. */
    readonly disabled = input<boolean>(false);

    /** Emite o termo pesquisado. */
    readonly search = output<SearchInputEvent>();

    /** Emite ao cancelar a busca, com o último termo. */
    readonly cancel = output<SearchInputCancelEvent>();

    protected formControl = new FormControl<string>('');
    protected actions = signal<TextInputAction[]>([
        { id: 'search', fontIcon: 'fa-solid fa-magnifying-glass' },
        { id: 'cancel', fontIcon: 'fa-solid fa-xmark' },
    ]);

    private manualSearch$ = new Subject<void>();

    constructor(private destroyRef: DestroyRef) {}

    ngOnChanges(changes: SimpleChanges) {
        if (changes['disabled']) {
            const disabled = changes['disabled'].currentValue ?? false;
            if (this.formControl) {
                if (disabled) {
                    this.formControl.disable();
                } else {
                    this.formControl.enable();
                }
            }
        }
    }

    ngOnInit(): void {
        this.handleSearch();
        this.actions.set([
            { id: 'cancel', fontIcon: 'fa-solid fa-xmark' },
            { id: 'search', fontIcon: 'fa-solid fa-magnifying-glass' },
        ]);
    }

    ngOnDestroy(): void {
        this.manualSearch$.complete();
    }

    private handleSearch(): void {
        const debouncedValueChanges$ = this.formControl.valueChanges.pipe(
            debounceTime(1000),
            filter(() => this.realTimeSearch()),
        );

        const manualSearchWithValue$ = this.manualSearch$.pipe(
            map(() => this.formControl.value),
        );

        merge(debouncedValueChanges$, manualSearchWithValue$)
            .pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: () => {
                    this.emitSearchEvent();
                },
            });
    }

    /**
     * Tratar o evento de digitação no campo de pesquisa.
     * Quando for Enter, emite o evento com o texto digitado.
     *
     * @param event Evento que é lançado quando o usuário pressiona
     *  uma tecla no campo de pesquisa.
     */
    protected onKeyUp(event: Event): void {
        const e = event as KeyboardEvent;
        event.preventDefault();

        if (e.key === 'Enter' && !e.shiftKey) {
            this.manualSearch$.next();
        } else if (e.key === 'Escape') {
            this.emitCancelEvent();
        }
    }

    /**
     * Emite o evento de pesquisa.
     *
     */
    private emitSearchEvent(): void {
        if (this.formControl.value!.trim() === '') {
            this.search.emit({ query: '' });
        } else if (
            this.formControl.value!.trim().length >=
            (this.minSize() || SearchInput.MIN_SIZE)
        ) {
            this.search.emit({ query: this.formControl.value! });
        }
    }

    /**
     * Emite o evento de cancelamento.
     */
    private emitCancelEvent(): void {
        this.cancel.emit({ lastQuery: this.formControl.value });
        this.formControl.setValue('');
    }

    /**
     * Trata os eventos de ação lançados pelo componente ai-text-input.
     *
     * @param action Ação que foi executada.
     * @protected
     */
    protected onActionPerformed(action: TextInputAction): void {
        if (action.id === 'search') {
            this.emitSearchEvent();
        } else if (action.id === 'cancel') {
            this.emitCancelEvent();
        }
    }
}
